import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNotification } from '../contexts/NotificationContext';
import { getApiUrl } from '../utils/api';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line } from 'recharts';
import QAActivityCard from './QAActivityCard';
import './Dashboard.css';

const DASHBOARD_SELECTED_PROJECT_KEY = 'dashboard:selectedProject';

const Dashboard = () => {
  const { showError } = useNotification();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(() => {
    try {
      return localStorage.getItem(DASHBOARD_SELECTED_PROJECT_KEY) || '';
    } catch (_) {
      return '';
    }
  });
  const [milestones, setMilestones] = useState([]);
  const [selectedMilestone, setSelectedMilestone] = useState('');
  const [stats, setStats] = useState(null);
  const [caseStats, setCaseStats] = useState(null);
  const [issuesStats, setIssuesStats] = useState(null);
  const [advancedStats, setAdvancedStats] = useState(null);
  const dashboardReqRef = useRef(0);
  const caseReqRef = useRef(0);
  const issuesReqRef = useRef(0);
  const advancedReqRef = useRef(0);

  const progress = stats?.progress || { planned: 0, in_progress: 0, completed: 0 };
  const pieData = [
    { name: 'Planejados', value: progress.planned, color: '#e5e7eb' },
    { name: 'Em Andamento', value: progress.in_progress, color: '#3b82f6' },
    { name: 'Concluídos', value: progress.completed, color: '#22c55e' }
  ];
  const totalExecutions = progress.planned + progress.in_progress + progress.completed;
  const lastFailureDate = progress.last_critical_failure 
    ? new Date(progress.last_critical_failure).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }) 
    : '-';

  const fetchProjects = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl('projects'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Falha ao buscar projetos');
      const data = await res.json();
      setProjects(Array.isArray(data?.projects) ? data.projects : data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchMilestones = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = getApiUrl('milestones');
      const url = new URL(apiUrl, window.location.origin);
      if (selectedProject) url.searchParams.set('project_id', selectedProject);

      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Falha ao buscar milestones');
      const data = await res.json();
      setMilestones(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setMilestones([]);
    }
  }, [selectedProject]);

  const fetchDashboardStats = useCallback(async () => {
    const reqId = ++dashboardReqRef.current;
    try {
      const token = localStorage.getItem('token');
      const apiUrl = getApiUrl('reports/dashboard');
      const url = new URL(apiUrl, window.location.origin);
      if (selectedProject) url.searchParams.set('projectId', selectedProject);
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) throw new Error('Falha ao buscar estatísticas do dashboard');
      const data = await res.json();
      if (reqId !== dashboardReqRef.current) return;
      setStats(data?.data || null);
    } catch (e) {
      if (reqId !== dashboardReqRef.current) return;
      console.error(e);
      showError('Erro ao carregar estatísticas do dashboard');
    }
  }, [showError, selectedProject]);

  const fetchTestCaseStats = useCallback(async () => {
    const reqId = ++caseReqRef.current;
    if (!selectedProject) { setCaseStats(null); return; }
    try {
      const token = localStorage.getItem('token');
      const apiUrl = getApiUrl('reports/test-cases');
      const url = new URL(apiUrl, window.location.origin);
      url.searchParams.set('project_id', selectedProject);
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` }});
      if (!res.ok) throw new Error('Falha ao buscar estatísticas de casos de teste');
      const data = await res.json();
      if (reqId !== caseReqRef.current) return;
      setCaseStats(data?.data || null);
    } catch (e) {
      if (reqId !== caseReqRef.current) return;
      console.error(e);
    }
  }, [selectedProject]);

  const fetchIssuesStats = useCallback(async () => {
    const reqId = ++issuesReqRef.current;
    try {
      const token = localStorage.getItem('token');
      const apiUrl = getApiUrl('reports/issues-stats');
      const url = new URL(apiUrl, window.location.origin);
      if (selectedProject) url.searchParams.set('projectId', selectedProject);
      
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` }});
      if (!res.ok) throw new Error('Falha ao buscar estatísticas de bugs/melhorias');
      const data = await res.json();
      if (reqId !== issuesReqRef.current) return;
      setIssuesStats(data?.data || null);
    } catch (e) {
      if (reqId !== issuesReqRef.current) return;
      console.error(e);
    }
  }, [selectedProject]);

  const fetchAdvancedStats = useCallback(async () => {
    const reqId = ++advancedReqRef.current;
    try {
      const token = localStorage.getItem('token');
      const apiUrl = getApiUrl('reports/dashboard-quality');
      const url = new URL(apiUrl, window.location.origin);
      if (selectedProject) url.searchParams.set('projectId', selectedProject);
      if (selectedMilestone) url.searchParams.set('milestoneId', selectedMilestone);

      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` }});
      if (!res.ok) throw new Error('Falha ao buscar métricas avançadas');
      const data = await res.json();
      if (reqId !== advancedReqRef.current) return;
      setAdvancedStats(data?.data || null);
    } catch (e) {
      if (reqId !== advancedReqRef.current) return;
      console.error(e);
      setAdvancedStats(null);
    }
  }, [selectedProject, selectedMilestone]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    try {
      if (selectedProject) {
        localStorage.setItem(DASHBOARD_SELECTED_PROJECT_KEY, selectedProject);
      } else {
        localStorage.removeItem(DASHBOARD_SELECTED_PROJECT_KEY);
      }
    } catch (_) {}
  }, [selectedProject]);

  useEffect(() => {
    if (!selectedProject || projects.length === 0) return;
    const exists = projects.some(project => String(project.id) === String(selectedProject));
    if (!exists) setSelectedProject('');
  }, [projects, selectedProject]);

  useEffect(() => {
    setSelectedMilestone('');
    fetchMilestones();
  }, [selectedProject, fetchMilestones]);

  useEffect(() => {
    if (!selectedMilestone) return;
    const exists = milestones.some(milestone => String(milestone.id) === String(selectedMilestone));
    if (!exists) setSelectedMilestone('');
  }, [milestones, selectedMilestone]);

  useEffect(() => {
    Promise.all([fetchDashboardStats(), fetchIssuesStats(), fetchTestCaseStats(), fetchAdvancedStats()]);
  }, [selectedProject, fetchTestCaseStats, fetchIssuesStats, fetchDashboardStats, fetchAdvancedStats]);

  const totalScenarios = (() => {
    if (!selectedProject) return stats?.total_test_cases || 0;
    if (caseStats?.by_status) {
      return Object.values(caseStats.by_status).reduce((sum, n) => sum + (parseInt(n) || 0), 0);
    }
    if (caseStats?.by_project && Array.isArray(caseStats.by_project) && caseStats.by_project.length > 0) {
      return parseInt(caseStats.by_project[0].total) || 0;
    }
    return 0;
  })();

  const priorityLabels = {
    highest: 'Muito alta',
    critical: 'Crítica',
    high: 'Alta',
    medium: 'Média',
    low: 'Baixa',
    lowest: 'Muito baixa'
  };

  const statusLabels = {
    completed: 'Concluído',
    passed: 'Aprovado',
    bug: 'Bug',
    failed: 'Falha',
    improvement: 'Melhoria',
    planned: 'Planejado',
    pending: 'Pendente',
    running: 'Executando',
    retest: 'Reteste',
    blocked: 'Bloqueado',
    skipped: 'Ignorado'
  };

  const resultStatusChart = Object.entries(advancedStats?.quality?.result_status || {})
    .filter(([, value]) => Number(value) > 0)
    .map(([key, value]) => ({
      name: statusLabels[key] || key,
      value: Number(value) || 0,
      color: key === 'completed' || key === 'passed' ? '#22c55e'
        : key === 'bug' || key === 'failed' ? '#ef4444'
        : key === 'improvement' ? '#f59e0b'
        : key === 'running' ? '#06b6d4'
        : '#94a3b8'
    }));

  const priorityChart = Object.entries(advancedStats?.risk?.by_priority || {})
    .map(([key, value]) => ({
      name: priorityLabels[key] || key,
      value: Number(value) || 0
    }))
    .filter(item => item.value > 0);

  const priorityColors = {
    highest: '#dc2626',
    critical: '#dc2626',
    high: '#ef4444',
    medium: '#f59e0b',
    low: '#94a3b8',
    lowest: '#94a3b8'
  };
  // Ordem de severidade (mais grave primeiro), não a ordem que vier do banco
  const priorityOrder = ['highest', 'critical', 'high', 'medium', 'low', 'lowest'];
  const bugPriorityChart = priorityOrder
    .map(key => ({
      key,
      name: priorityLabels[key] || key,
      value: Number(advancedStats?.risk?.bugs_by_priority?.[key]) || 0,
      color: priorityColors[key]
    }))
    .filter(item => item.value > 0);
  const criticalBugsOpen = ['highest', 'critical']
    .reduce((sum, key) => sum + (Number(advancedStats?.risk?.bugs_by_priority?.[key]) || 0), 0);

  const coverageChart = (advancedStats?.coverage?.by_folder || [])
    .map(item => ({
      name: item.folder_name,
      cobertura: Number(item.coverage) || 0,
      vinculados: Number(item.linked) || 0,
      total: Number(item.total) || 0
    }));

  const flowChart = advancedStats?.flow?.created_vs_resolved || [];
  const throughputChart = advancedStats?.flow?.execution_throughput || [];
  const bugsByDevChart = advancedStats?.risk?.bugs_by_dev || [];
  const mttr = advancedStats?.risk?.mttr || null;
  const release = advancedStats?.release || {};
  const releaseStatus = release?.is_ready ? 'Pronta' : release?.milestone ? 'Atenção' : 'Sem milestone';

  return (
    <div className="dashboard">
      <section className="dash-hero" aria-labelledby="dashboard-title">
        <div className="dash-hero-content">
          <div>
            <h1 id="dashboard-title">📊 Dashboard de Testes</h1>
            <p>Visão geral de métricas, KPIs e atividades dos seus projetos de QA.</p>
          </div>
        </div>
      </section>

      <div className="dash-header">
        <div className="dash-controls">
          <select 
            value={selectedProject} 
            onChange={(e) => setSelectedProject(e.target.value)}
          >
            <option value="">Todos os projetos</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="dash-grid executive">
        <div className="card kpi-card">
          <div className="kpi-label">Taxa de sucesso</div>
          <div className="kpi-value">{advancedStats?.quality?.success_rate ?? 0}%</div>
          <div className="kpi-subtext">{advancedStats?.quality?.successful_results ?? 0} resultados aprovados/concluídos</div>
        </div>
        <div className="card kpi-card risk">
          <div className="kpi-label">Bugs abertos</div>
          <div className="kpi-value">{advancedStats?.risk?.open_bugs ?? 0}</div>
          <div className={`kpi-critical-badge${criticalBugsOpen > 0 ? ' kpi-critical-badge--alert' : ''}`}>
            {criticalBugsOpen > 0 && <span>🔥</span>}
            {criticalBugsOpen} crítico{criticalBugsOpen === 1 ? '' : 's'}
          </div>
          {bugPriorityChart.length > 0 && (
            <div className="kpi-priority-breakdown">
              {bugPriorityChart.map(item => (
                <span key={item.key} className="kpi-priority-chip" style={{ '--chip-color': item.color }}>
                  {item.name} <b>{item.value}</b>
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="card kpi-card">
          <div className="kpi-label">Cobertura executada</div>
          <div className="kpi-value">{advancedStats?.coverage?.coverage_rate ?? 0}%</div>
          <div className="kpi-subtext">{advancedStats?.coverage?.executed_cases ?? 0} de {advancedStats?.coverage?.total_cases ?? 0} cenários</div>
        </div>
        <div className={`card kpi-card release ${release?.is_ready ? 'ready' : 'attention'}`}>
          <div className="kpi-label">Readiness da release</div>
          <div className="kpi-value">{release?.readiness_score ?? 0}</div>
          <div className="kpi-subtext">{releaseStatus}{release?.milestone?.name ? ` • ${release.milestone.name}` : ''}</div>
        </div>
      </div>

      <div className="dash-grid top">
        <div className="card metric" style={{ display: 'flex', flexDirection: 'column' }}>
          <div className="metric-title">Projetos</div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div className="metric-value">{stats ? stats.total_projects : 0}</div>
            <div className="metric-bars">
              <div className="bar green" style={{ width: `${stats ? Math.round(((stats.active_projects || 0) / Math.max(1, stats.total_projects || 1)) * 100) : 0}%` }} />
              <div className="bar purple" style={{ width: `${stats ? Math.round(((stats.completed_projects || 0) / Math.max(1, stats.total_projects || 1)) * 100) : 0}%` }} />
              <div className="bar red" style={{ width: `${stats ? Math.round(((stats.inactive_projects || 0) / Math.max(1, stats.total_projects || 1)) * 100) : 0}%` }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '11px', color: '#666' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3ddc97', marginRight: '4px' }}></div>
                {stats ? (stats.active_projects || 0) : 0} Ativos
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#8b5cf6', marginRight: '4px' }}></div>
                {stats ? (stats.completed_projects || 0) : 0} Concluídos
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef6b6b', marginRight: '4px' }}></div>
                {stats ? (stats.inactive_projects || 0) : 0} Inativos
              </div>
            </div>
          </div>
        </div>

        <div className="card metric" style={{ display: 'flex', flexDirection: 'column' }}>
          <div className="metric-title">Total de Cenários</div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div className="metric-value">{totalScenarios ?? stats?.total_test_cases ?? 0}</div>
            
            <div style={{ width: '100%', height: '8px', background: '#e0e0e0', borderRadius: '4px', overflow: 'hidden', display: 'flex', marginTop: '10px' }}>
              <div style={{ width: `${(totalScenarios ?? stats?.total_test_cases ?? 0) > 0 ? ((stats?.active_cycle_test_cases || 0) / (totalScenarios ?? stats?.total_test_cases ?? 1)) * 100 : 0}%`, background: '#4caf50', height: '100%' }} />
              <div style={{ width: `${(totalScenarios ?? stats?.total_test_cases ?? 0) > 0 ? (Math.max(0, (totalScenarios ?? stats?.total_test_cases ?? 0) - (stats?.active_cycle_test_cases || 0)) / (totalScenarios ?? stats?.total_test_cases ?? 1)) * 100 : 0}%`, background: '#3b82f6', height: '100%' }} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '11px', color: '#666' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#4caf50', marginRight: '4px' }} />
                {stats?.active_cycle_test_cases || 0} vinculados
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3b82f6', marginRight: '4px' }} />
                {Math.max(0, (totalScenarios ?? stats?.total_test_cases ?? 0) - (stats?.active_cycle_test_cases || 0))} disponíveis
              </div>
            </div>
          </div>
        </div>

        <div className="card cycle" style={{ display: 'flex', flexDirection: 'column', padding: '15px' }}>
          <div className="cycle-title" style={{ marginBottom: '10px' }}>Processos dos cards em Execuções</div>
          <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <div style={{ width: '120px', height: '120px', position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={35}
                    outerRadius={55}
                    paddingAngle={2}
                    dataKey="value"
                    stroke="none"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div style={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                fontSize: '14px', fontWeight: 'bold', color: '#333'
              }}>
                {Math.round((progress.completed / (totalExecutions || 1)) * 100)}%
              </div>
            </div>
            
            <div style={{ flex: 1, marginLeft: '20px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {pieData.map(item => (
                  <div key={item.name} style={{ display: 'flex', alignItems: 'center', fontSize: '13px' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: item.color, marginRight: '8px' }}></div>
                    <span style={{ flex: 1, color: '#555' }}>{item.name}:</span>
                    <span style={{ fontWeight: '600' }}>{item.value}</span>
                  </div>
                ))}
                <div style={{ borderTop: '1px solid #eee', marginTop: '5px', paddingTop: '5px', display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                  <span style={{ color: '#555' }}>Total:</span>
                  <span style={{ fontWeight: 'bold' }}>{totalExecutions}</span>
                </div>
              </div>
              
              <div style={{ marginTop: '12px' }}>
                <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Última falha crítica</div>
                <div style={{ fontSize: '13px', fontWeight: '500', color: '#dc3545' }}>{lastFailureDate}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="dash-grid middle">
        <QAActivityCard projectId={selectedProject} />

        <div className="card tasks bm-card">
          <div className="tasks-title">
            <span className="bm-title-icon">🎯</span> Bugs & Melhorias Ativos
          </div>

          {issuesStats ? (
            <div className="bm-body">
              <div className="bm-total">
                <div className="bm-total-value">{issuesStats.total_open}</div>
                <div className="bm-total-label">itens abertos no total</div>
              </div>

              <div className="bm-section">
                <div className="bm-section-title bm-section-title--alert">
                  <span>🔥</span> Prioridade alta
                </div>
                <div className="bm-stat-row">
                  <div className="bm-stat">
                    <div className="bm-stat-value">{issuesStats.high_priority?.bugs?.count || 0}</div>
                    <div className="bm-stat-label">bugs</div>
                    <div className="bm-stat-meta">média {Math.round(issuesStats.high_priority?.bugs?.avg_days || 0)}d aberto</div>
                  </div>
                  <div className="bm-stat">
                    <div className="bm-stat-value">{issuesStats.high_priority?.improvements?.count || 0}</div>
                    <div className="bm-stat-label">melhorias</div>
                    <div className="bm-stat-meta">média {Math.round(issuesStats.high_priority?.improvements?.avg_days || 0)}d aberta</div>
                  </div>
                </div>
              </div>

              <div className="bm-section">
                <div className="bm-section-title">
                  <span>📊</span> Tempo em aberto
                </div>
                <div className="bm-section-subtitle">Há quanto tempo os itens abertos estão sem solução</div>
                {(() => {
                  const under7 = issuesStats.aging?.under_7 || 0;
                  const between = issuesStats.aging?.between_7_30 || 0;
                  const over30 = issuesStats.aging?.over_30 || 0;
                  const agingTotal = under7 + between + over30;
                  const pct = (n) => agingTotal ? (n / agingTotal) * 100 : 0;
                  const rows = [
                    { key: 'under7', color: '#22c55e', label: 'Recente', range: 'até 7 dias', value: under7 },
                    { key: 'between', color: '#f59e0b', label: 'Atenção', range: '7 a 30 dias', value: between },
                    { key: 'over30', color: '#ef4444', label: 'Crítico', range: 'mais de 30 dias', value: over30 }
                  ];
                  return (
                    <>
                      <div className="bm-aging-bar">
                        {under7 > 0 && <div className="bm-aging-seg" style={{ width: `${pct(under7)}%`, background: '#22c55e' }} title={`Recente (até 7 dias): ${under7}`} />}
                        {between > 0 && <div className="bm-aging-seg" style={{ width: `${pct(between)}%`, background: '#f59e0b' }} title={`Atenção (7 a 30 dias): ${between}`} />}
                        {over30 > 0 && <div className="bm-aging-seg" style={{ width: `${pct(over30)}%`, background: '#ef4444' }} title={`Crítico (mais de 30 dias): ${over30}`} />}
                      </div>
                      <div className="bm-aging-list">
                        {rows.map(row => (
                          <div className="bm-aging-row" key={row.key}>
                            <span className="bm-aging-dot" style={{ background: row.color }} />
                            <span className="bm-aging-row-text">
                              {row.label} <span className="bm-aging-row-range">({row.range})</span>
                            </span>
                            <span className="bm-aging-row-value">{row.value}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>

              {mttr && (mttr.bugs.resolved_count > 0 || mttr.improvements.resolved_count > 0) && (
                <div className="bm-section">
                  <div className="bm-section-title">
                    <span>⏱️</span> Tempo até resolução
                  </div>
                  <div className="bm-section-subtitle">Média de dias entre abrir e resolver, nos itens já fechados</div>
                  <div className="bm-stat-row">
                    <div className="bm-stat">
                      <div className="bm-stat-value">{mttr.bugs.avg_days}d</div>
                      <div className="bm-stat-label">bugs</div>
                      <div className="bm-stat-meta">{mttr.bugs.resolved_count} resolvido{mttr.bugs.resolved_count === 1 ? '' : 's'}</div>
                    </div>
                    <div className="bm-stat">
                      <div className="bm-stat-value">{mttr.improvements.avg_days}d</div>
                      <div className="bm-stat-label">melhorias</div>
                      <div className="bm-stat-meta">{mttr.improvements.resolved_count} resolvida{mttr.improvements.resolved_count === 1 ? '' : 's'}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bm-loading">Carregando métricas...</div>
          )}
        </div>
      </div>

      <div className="dash-grid insights">
        <div className="card insight-card quality-card">
          <div className="insight-header">
            <div>
              <div className="insight-title">Saúde da Qualidade</div>
              <div className="insight-subtitle">Distribuição dos resultados dos cenários</div>
            </div>
            <span className="insight-pill">{advancedStats?.quality?.bugs_per_execution ?? 0} bugs/execução</span>
          </div>
          <div className="quality-layout">
            <div className="quality-chart">
              <ResponsiveContainer width="100%" height={210}>
                <PieChart>
                  <Pie data={resultStatusChart} cx="50%" cy="50%" innerRadius={54} outerRadius={82} dataKey="value" stroke="none">
                    {resultStatusChart.map((entry, index) => (
                      <Cell key={`quality-cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="quality-list">
              {resultStatusChart.length === 0 ? (
                <div className="empty-metric">Sem resultados para exibir</div>
              ) : resultStatusChart.map(item => (
                <div key={item.name} className="quality-row">
                  <span className="dot" style={{ background: item.color }} />
                  <span>{item.name}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card insight-card">
          <div className="insight-header">
            <div>
              <div className="insight-title">Risco Atual</div>
              <div className="insight-subtitle">Bugs e melhorias abertos por severidade</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={priorityChart} layout="vertical" margin={{ top: 8, right: 20, bottom: 8, left: 24 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f7" />
              <XAxis type="number" allowDecimals={false} />
              <YAxis dataKey="name" type="category" width={78} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" fill="#ef4444" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="dash-grid diagnostic">
        <div className="card insight-card">
          <div className="insight-header">
            <div>
              <div className="insight-title">Cobertura por Funcionalidade</div>
              <div className="insight-subtitle">Percentual de cenários vinculados/executáveis por pasta</div>
            </div>
            <span className="insight-pill">{advancedStats?.coverage?.linked_rate ?? 0}% vinculados</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={coverageChart} margin={{ top: 8, right: 18, bottom: 36, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
              <XAxis dataKey="name" angle={-20} textAnchor="end" height={58} tick={{ fontSize: 10 }} interval={0} />
              <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={(value, name) => name === 'cobertura' ? [`${value}%`, 'Cobertura'] : [value, name]} />
              <Bar dataKey="cobertura" fill="#3b82f6" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card insight-card">
          <div className="insight-header">
            <div>
              <div className="insight-title">Fluxo de Correção</div>
              <div className="insight-subtitle">Bugs/melhorias criados vs resolvidos nas últimas semanas</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={flowChart} margin={{ top: 12, right: 18, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="week" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="created" name="Criados" stroke="#ef4444" strokeWidth={3} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="resolved" name="Resolvidos" stroke="#22c55e" strokeWidth={3} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="dash-grid diagnostic">
        <div className="card insight-card">
          <div className="insight-header">
            <div>
              <div className="insight-title">Vazão de Execuções</div>
              <div className="insight-subtitle">Execuções concluídas por semana — mostra se o time tá acelerando ou travando</div>
            </div>
          </div>
          {throughputChart.length === 0 ? (
            <div className="empty-metric">Nenhuma execução concluída nas últimas 8 semanas.</div>
          ) : (
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={throughputChart} margin={{ top: 8, right: 18, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="completed" name="Concluídas" fill="#3b82f6" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="card insight-card">
          <div className="insight-header">
            <div>
              <div className="insight-title">Bugs por Responsável</div>
              <div className="insight-subtitle">Bugs abertos atribuídos a cada dev — pra enxergar concentração de carga</div>
            </div>
          </div>
          {bugsByDevChart.length === 0 ? (
            <div className="empty-metric">Nenhum bug aberto com responsável no escopo.</div>
          ) : (
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={bugsByDevChart} layout="vertical" margin={{ top: 8, right: 20, bottom: 8, left: 24 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f7" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" name="Bugs" fill="#f59e0b" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="dash-grid release-risk">
        <div className="card release-card">
          <div className="release-card-header">
            <div className="release-score">
              <div className="score-circle" style={{ background: `conic-gradient(${release?.is_ready ? '#22c55e' : '#f59e0b'} ${(release?.readiness_score || 0) * 3.6}deg, #e5e7eb 0deg)` }}>
                <div>{release?.readiness_score ?? 0}</div>
              </div>
              <div>
                <div className="insight-title">Qualidade da Release/Milestone</div>
                <div className="insight-subtitle">{release?.milestone?.name || 'Nenhuma milestone encontrada no escopo'}</div>
                <div className={`release-badge ${release?.is_ready ? 'ready' : 'attention'}`}>{releaseStatus}</div>
              </div>
            </div>
            <select
              className="release-filter-select"
              value={selectedMilestone}
              onChange={(e) => setSelectedMilestone(e.target.value)}
              title="Filtrar por release/milestone"
            >
              <option value="">Release/Milestone: mais recente</option>
              {milestones.map(milestone => (
                <option key={milestone.id} value={milestone.id}>
                  {milestone.name}{!selectedProject && milestone.project_name ? ` • ${milestone.project_name}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="release-checklist">
            <div><span className={release?.completion_rate >= 90 ? 'check ok' : 'check warn'} /> Execuções concluídas: <strong>{release?.completion_rate ?? 0}%</strong></div>
            <div><span className={(release?.critical_open || 0) === 0 ? 'check ok' : 'check err'} /> Bugs críticos abertos: <strong>{release?.critical_open ?? 0}</strong></div>
            <div><span className={(release?.open_issues || 0) === 0 ? 'check ok' : 'check warn'} /> Bugs/melhorias pendentes: <strong>{release?.open_issues ?? 0}</strong></div>
          </div>
        </div>

        <div className="card top-risks-card">
          <div className="insight-title">Top 5 Riscos</div>
          <div className="insight-subtitle">Priorizado por severidade e tempo em aberto</div>
          <div className="risk-list">
            {(advancedStats?.risk?.top_risks || []).length === 0 ? (
              <div className="empty-metric">Nenhum risco aberto no escopo.</div>
            ) : advancedStats.risk.top_risks.map(item => (
              <div key={item.id} className="risk-item">
                <div className="risk-main">
                  <strong>{item.title}</strong>
                  <span>{item.project_name} • {item.responsible_name}</span>
                </div>
                <div className="risk-meta">
                  <span>{priorityLabels[item.priority] || item.priority}</span>
                  <strong>{item.age_days}d</strong>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="dash-grid bottom">
          {stats?.last_executions && stats.last_executions.length > 0 ? (
            stats.last_executions.map((exec, idx) => (
              <div className="card last small" key={idx}>
                <div className="last-title">{idx === 0 ? 'Última execução por' : 'Penúltima execução por'}</div>
                <div className="last-user">{exec.user}</div>
                <div className="last-date">
                  {new Date(exec.date).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })}, {new Date(exec.date).toLocaleTimeString('pt-BR', {hour: '2-digit', minute:'2-digit'})}
                </div>
              </div>
            ))
          ) : (
            <>
              <div className="card last small">
                <div className="last-title">Última execução por</div>
                <div className="last-user">-</div>
                <div className="last-date">-</div>
              </div>
              <div className="card last small">
                <div className="last-title">Penúltima execução por</div>
                <div className="last-user">-</div>
                <div className="last-date">-</div>
              </div>
            </>
          )}
      </div>
    </div>
  );
};

export default Dashboard;
